import { Button, Chip, Spinner } from '@heroui/react';
import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

import { ErrorCard } from '../../../../components/ErrorCard';
import { SidePanel } from '../../../../components/SidePanel';
import { useMemberWorkplaces } from '../../hooks';
import type { MemberWorkspace, Workplace } from '../../types';
import { locationLabel, tierStyle } from '../../utils';

export interface MemberWorkplacesPanelProps {
  /** The workspace the member is being read within. */
  workspaceId: string;
  /** The member being read. Null closes the panel and skips the read. */
  userId: string | null;
  onClose: () => void;
}

const childrenOf = (
  workplaces: readonly Workplace[]
): Map<string | null, Workplace[]> =>
  workplaces.reduce((byParent, workplace) => {
    const key = workplace.parentLocationId;

    return byParent.set(key, [...(byParent.get(key) ?? []), workplace]);
  }, new Map<string | null, Workplace[]>());

/** One step of nesting. Every row pays it per level, so depth stays readable. */
const INDENT_PX = 20;

const WorkplaceRow: React.FC<{
  workplace: Workplace;
  byParent: Map<string | null, Workplace[]>;
  collapsed: Set<string>;
  onToggle: (locationId: string) => void;
  depth: number;
}> = ({ workplace, byParent, collapsed, onToggle, depth }) => {
  const tier = tierStyle(workplace.hierarchyType);
  const children = byParent.get(workplace.locationId) ?? [];
  const isCollapsed = collapsed.has(workplace.locationId);
  const name = locationLabel(workplace.locationName, workplace.locationId);

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-default-100 py-2">
        <div
          className="flex min-w-0 items-center gap-2"
          style={{ paddingLeft: depth * INDENT_PX }}
        >
          <Button
            isIconOnly
            size="sm"
            variant="light"
            radius="sm"
            className="h-6 w-6 min-w-6 shrink-0"
            isDisabled={children.length === 0}
            aria-label={
              isCollapsed
                ? `Show the places under ${name}`
                : `Hide the places under ${name}`
            }
            onPress={() => onToggle(workplace.locationId)}
          >
            {isCollapsed ? (
              <FiChevronRight size={14} />
            ) : (
              <FiChevronDown size={14} />
            )}
          </Button>
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="truncate text-small font-medium text-foreground">
              {name}
            </p>
            <p className="text-tiny text-default-400">
              {tier.label}
              {children.length > 0 &&
                isCollapsed &&
                ` · ${children.length} below`}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          {workplace.packages.map((pkg) => (
            <Chip key={pkg} size="sm" variant="flat">
              {pkg}
            </Chip>
          ))}
        </div>
      </div>
      {!isCollapsed &&
        children.map((child) => (
          <WorkplaceRow
            key={child.locationId}
            workplace={child}
            byParent={byParent}
            collapsed={collapsed}
            onToggle={onToggle}
            depth={depth + 1}
          />
        ))}
    </>
  );
};

const WorkplacesBody: React.FC<{ memberWorkspace: MemberWorkspace }> = ({
  memberWorkspace,
}) => {
  const { workspaceName, workplaces } = memberWorkspace;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const byParent = childrenOf(workplaces);
  // A parent id is set only when that place is listed too, so anything with a
  // null parent is a top of the returned forest.
  const roots = byParent.get(null) ?? [];

  const toggle = (locationId: string) =>
    setCollapsed((open) => {
      const next = new Set(open);
      if (!next.delete(locationId)) {
        next.add(locationId);
      }

      return next;
    });

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <p className="text-tiny uppercase tracking-wide text-default-400">
          Workspace
        </p>
        <p className="text-small text-foreground">{workspaceName}</p>
      </div>

      <div>
        <p className="mb-1 text-tiny uppercase tracking-wide text-default-400">
          Places they can work
        </p>
        {workplaces.length === 0 ? (
          <p className="py-3 text-small text-default-500">
            They are in this workspace and cannot work anywhere in it yet.
          </p>
        ) : (
          roots.map((workplace) => (
            <WorkplaceRow
              key={workplace.locationId}
              workplace={workplace}
              byParent={byParent}
              collapsed={collapsed}
              onToggle={toggle}
              depth={0}
            />
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Where one person can work, as their own place selector shows it: the
 * workspace, then every place in it they may work at and what they may do
 * there. A place they can do nothing at is not listed, so this is their reach
 * rather than the workspace's shape.
 */
export const MemberWorkplacesPanel: React.FC<MemberWorkplacesPanelProps> = ({
  workspaceId,
  userId,
  onClose,
}) => {
  const { memberWorkspace, isLoading, error, notAMember } = useMemberWorkplaces(
    workspaceId,
    userId
  );

  return (
    <SidePanel
      isOpen={userId !== null}
      onClose={onClose}
      title="Where they can work"
      size="md"
    >
      {error && (
        <div className="p-6">
          <ErrorCard
            title="Could not load their places"
            message={error.message}
          />
        </div>
      )}

      {!error && notAMember && (
        <div className="p-6">
          <ErrorCard
            title="Not in a workspace"
            message="They have left since the directory was read."
          />
        </div>
      )}

      {/* The read is skipped while closed, so a spinner only shows for a real
          question in flight. */}
      {!error && !notAMember && !memberWorkspace && isLoading && (
        <div className="flex justify-center p-10">
          <Spinner size="sm" />
        </div>
      )}

      {!error && !notAMember && memberWorkspace && (
        <WorkplacesBody memberWorkspace={memberWorkspace} />
      )}
    </SidePanel>
  );
};
